import React, { FC, useEffect, useState } from 'react';
import { SectionWrapper } from '../../SectionWrapper';
import { checkIsFileValid, DocumentsUploader } from '../DocumentsUploader';
import { DocumentsUploaderTable } from '../DocumentsUploaderTable';
import { IModalWindow, ModalWindow } from '../../modal/ModalWindow';
import { useTranslation } from 'react-i18next';
import { ISetDocuments } from '../../../store/merchnts/actions/application';
import { requestErrorHandler } from '../../../utils/newApplicationUtils';
import clsx from 'clsx';

interface Props {
  enableEditBlock: boolean;
  dataUploadedDocuments: any;
  id: string;
  onEditSuccess?: () => Promise<void> | void;
  enableToDelete?: boolean;
  setDocuments?: (data: ISetDocuments) => Promise<void>;
  tableLoading?: boolean;
  showPlaceholder: boolean;
}
export const DocumentsUploaderPage: FC<Props> = (props) => {
  const {
    enableEditBlock, dataUploadedDocuments,
    id, onEditSuccess,
    enableToDelete, setDocuments,
    tableLoading
  } = props;

  const { t } = useTranslation();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });

  const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

  useEffect(() => {
    if (dataUploadedDocuments?.length > 0) {
      setUploadedDocuments(dataUploadedDocuments);
    }
  }, [dataUploadedDocuments]);

  const handleEdit = async (): Promise<void> => {
    try {
      if (!fileList.length) {
        setIsEditMode(!isEditMode);
      }

      if (!checkIsFileValid(fileList)) return;

      if (uploadedDocuments?.length !== dataUploadedDocuments?.length || fileList?.length > 0) {
        setIsLoading(true);

        await setDocuments({
          id,
          fileList,
          uploadedFiles: uploadedDocuments,
        });

        if (onEditSuccess) {
          await onEditSuccess();
        }
        setFileList([]);
        setIsEditMode(!isEditMode);
        setIsLoading(false);
      }
    } catch (error) {
      console.log('handleEdit error', error);
      setIsLoading(false);
      requestErrorHandler(error);
    }

  };

  const handleCancel = (): void => {
    try {
      if (uploadedDocuments?.length !== dataUploadedDocuments?.length || fileList?.length > 0) {
        setModalState({
          isOpen: true,
          modalTitle: t('MERCHANT.CANCEL_MODAL_TITLE'),
          modalText: t('MERCHANT.CANCEL_MODAL_TEXT'),
          confirmButtonText: t('MERCHANT.CANCEL_MODAL_CONFIRM_BTN_NAME'),
          onConfirm: closeModal,
          onCancel: (): void => {
            closeModal();
            setIsEditMode(false);
            setUploadedDocuments(dataUploadedDocuments);
            setFileList([]);
          }
        });

      } else {
        setIsEditMode(!isEditMode);
      }
    } catch (error) {
      console.log('handleCancel error', error);
    }
  };

  return (
    <SectionWrapper
      showPlaceholder={props.showPlaceholder}
      title={t('SELF_ONBOARDING.SECTION_TITLE.DOCUMENT')}
      isEditBlock={enableEditBlock}
      isEditMode={isEditMode}
      setIsEditMode={setIsEditMode}
      onEditClick={handleEdit}
      onCancelClick={handleCancel}
      loading={isLoading}
      pageLoading={tableLoading}
      isCardBody
      sectionAdditionalClass="mb-10"
      sectionBodyAdditionalClass={clsx(!isEditMode && 'py-0')}
      additionalBodyStyles={{ 'minHeight': '203px' }}
    >
      {
        isEditMode
          ? <DocumentsUploader
              fileList={fileList}
              setFileList={setFileList}
              uploadedDocuments={uploadedDocuments}
              setUploadedDocuments={setUploadedDocuments}
              enableToDelete={enableToDelete}
            />
          : <DocumentsUploaderTable
              uploadedDocuments={uploadedDocuments}
              applicationId={id}
              showActions={true}
              isLoading={tableLoading}
            />
      }


      <ModalWindow
        {...modalState}
        onClose={closeModal}
      />
    </SectionWrapper>
  );
};
